import { Stack, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { CheckIcon } from '@/components/icons/CheckIcon';
import { ListIcon } from '@/components/icons/ListIcon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useFormContext } from '@/components/react-hook-form';
import { TestamentaryEntityAISuggestionsCountBadge } from '@/modules/aiSuggestions/components/TestamentaryEntityAISuggestionsCountBadge';
import { COLORS } from '@/styles/tokens/colors';

import { useEditTestamentaryEntitySplitScreenModalContext } from './contexts/editTestamentaryEntitySplitScreenModal.context';
import {
  EditTestamentaryEntityFormShape,
  EditTestamentaryEntitySection,
  EditTestamentaryEntitySectionOption,
} from './EditTestamentaryEntitySplitScreen.types';

const WIDTH = 280;

interface EditTestamentaryEntitySplitScreenFormSectionSwitcherProps {
  onSubmit: (afterSubmitSection?: EditTestamentaryEntitySection) => void;
}

export function EditTestamentaryEntitySplitScreenFormSectionSwitcher({
  onSubmit,
}: EditTestamentaryEntitySplitScreenFormSectionSwitcherProps) {
  const {
    currentSection,
    sectionOptions,
    clickedSubmitButton,
    setClickedSubmitButton,
  } = useEditTestamentaryEntitySplitScreenModalContext();

  const {
    formState: { isSubmitting },
  } = useFormContext<EditTestamentaryEntityFormShape>();

  const handleSectionSwitch = useCallback(
    (section: EditTestamentaryEntitySection) => {
      setClickedSubmitButton('section-switcher');
      onSubmit(section);
    },
    [onSubmit, setClickedSubmitButton]
  );

  const sectionLabel = useMemo(() => {
    if (!currentSection) {
      return 'Select section to edit';
    }
    const currentSectionOption = sectionOptions.find(
      (o) => o.section === currentSection
    );
    return currentSectionOption?.label ?? 'Select section to edit';
  }, [currentSection, sectionOptions]);

  const isSubmittingFormFromSwitcher =
    isSubmitting && clickedSubmitButton === 'section-switcher';

  return (
    <ButtonWithPopover
      data-testid="edit-testamentary-entity-section-switcher"
      contentBoxSx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
      }}
      size="sm"
      variant="secondary"
      popperVariant="menuBelow"
      startIcon={ListIcon}
      disabled={isSubmitting}
      loading={isSubmittingFormFromSwitcher}
      label={
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography
            variant="body-xs"
            color={COLORS.TEXT.PRIMARY}
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
          >
            {sectionLabel}
          </Typography>
          <TestamentaryEntityAISuggestionsCountBadge section={currentSection} />
        </Stack>
      }
    >
      {sectionOptions.map((o) => (
        <TestamentaryEntitySectionMenuItem
          key={o.section}
          section={o}
          onRequestSectionSwitch={handleSectionSwitch}
          disabled={isSubmitting}
        />
      ))}
    </ButtonWithPopover>
  );
}

interface TestamentaryEntitySectionMenuItemProps {
  section: EditTestamentaryEntitySectionOption;
  onRequestSectionSwitch: (section: EditTestamentaryEntitySection) => void;
  disabled: boolean;
}

function TestamentaryEntitySectionMenuItem({
  section,
  onRequestSectionSwitch,
  disabled,
}: TestamentaryEntitySectionMenuItemProps) {
  const { currentSection } = useEditTestamentaryEntitySplitScreenModalContext();

  const isSelected = section.section === currentSection;

  return (
    <MenuItem
      sx={{
        backgroundColor: isSelected ? COLORS.NAVY[50] : undefined,
        minWidth: WIDTH,
      }}
      justifyContent="space-between"
      label={
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="body-xs" color={COLORS.TEXT.PRIMARY}>
            {section.label}
          </Typography>
          <TestamentaryEntityAISuggestionsCountBadge
            section={section.section}
          />
        </Stack>
      }
      iconAfter={
        isSelected && (
          <CheckIcon size={16} color={COLORS.FUNCTIONAL.SUCCESS.DEFAULT} />
        )
      }
      onClick={() => onRequestSectionSwitch(section.section)}
      disabled={disabled}
    />
  );
}
